import { useEffect } from "react";
import type { Map as MapboxMap, GeoJSONSource } from "mapbox-gl";
import { useMap } from "../../contexts/MapContext";
import { SOURCE_LAYER } from "./fuelClassification";
import { getFuelFeatureInfo } from "./fuel3d/parseCache";
import { isStyleParsed, onStyleReady } from "../../utils/mapStyleReady";

/**
 * Film layer / source ids, per fuel source so a map with several 3D stands
 * (a folder story citing two projects) gets a film under each.
 */
const filmIdFor = (fuelSourceId: string) => `${fuelSourceId}-dim-film`;
/** The film's own GeoJSON source — the treed polygons it shades. */
const filmSourceIdFor = (fuelSourceId: string) => `${fuelSourceId}-dim-film-src`;
/**
 * Translucent film tone, mottled per polygon by a stable per-feature value `v`
 * (0..1) so the canopy reads as a natural patchwork rather than one flat olive.
 * Deliberately a DARK, low-saturation forest-floor ramp at high alpha: the lit
 * green crowns read against the ground only when the ground is clearly lower in
 * value than they are (a light olive film here washed the canopy out). The ramp
 * runs from a dark sage through a deep forest green to a dark khaki, and the
 * higher alpha mutes the busy/bright satellite detail showing through.
 */
const FOREST_FILM_COLOR = [
  "interpolate",
  ["linear"],
  ["get", "v"],
  0,
  "rgba(129, 141, 104, 0.86)",
  0.4,
  "rgba(116, 134, 98, 0.86)",
  0.7,
  "rgba(113, 141, 100, 0.86)",
  1,
  "rgba(121, 112, 84, 0.86)",
];
/**
 * Grassland floor tone — lighter, khaki/straw-green and a touch less opaque than
 * the forest floor, so the 3D grass tufts read against a flat grassy ground
 * (rather than raw satellite) without the darker forest film washing them out.
 */
const GRASS_FILM_COLOR = [
  "interpolate",
  ["linear"],
  ["get", "v"],
  0,
  "rgba(151, 154, 96, 0.74)",
  0.5,
  "rgba(138, 146, 84, 0.74)",
  1,
  "rgba(163, 150, 98, 0.74)",
];
/** Pick the floor tone per polygon: grass cells get the grass ramp. */
const FILM_FILL_COLOR = [
  "case",
  ["==", ["get", "grass"], 1],
  GRASS_FILM_COLOR,
  FOREST_FILM_COLOR,
];

/** Stable [0,1) hash → the mottle value carried on each film polygon. */
const hashUnit = (n: number): number => {
  let h = Math.imul((n | 0) ^ 0x9e3779b9, 2654435761) >>> 0;
  h = (h ^ (h >>> 15)) >>> 0;
  return h / 4294967296;
};

/** Deterministic variation per feature — from its id, else its first vertex. */
const featureVariation = (f: GeoJSON.Feature): number => {
  let key = typeof f.id === "number" ? f.id : 0;
  if (!key) {
    const g = f.geometry;
    const c =
      g.type === "Polygon"
        ? g.coordinates[0]?.[0]
        : g.type === "MultiPolygon"
        ? g.coordinates[0]?.[0]?.[0]
        : undefined;
    if (c) key = (Math.round(c[0] * 1e4) * 73856093) ^ (Math.round(c[1] * 1e4) * 19349663);
  }
  return hashUnit(key);
};

const EMPTY: GeoJSON.FeatureCollection = { type: "FeatureCollection", features: [] };

/** Base vector source in satellite-streets-v12; its layers are basemap detail. */
const BASE_VECTOR_SOURCE = "composite";
const OVERLAY_TYPES = [
  "fill",
  "line",
  "symbol",
  "circle",
  "fill-extrusion",
  "heatmap",
];

/**
 * The lowest app-overlay layer (a fill/line/symbol/… whose source is one of the
 * app's own sources, not the basemap's "composite" vector source). Inserting the
 * film just below it dims the basemap while leaving every app 2D layer crisp.
 */
const bottomMostOverlayId = (map: MapboxMap): string | undefined => {
  try {
    const layers = map.getStyle()?.layers ?? [];
    for (const lyr of layers) {
      if (lyr.id.endsWith("-dim-film")) continue;
      const src = (lyr as { source?: unknown }).source;
      if (
        typeof src === "string" &&
        src !== BASE_VECTOR_SOURCE &&
        !src.endsWith("-dim-film-src") &&
        OVERLAY_TYPES.includes(lyr.type as string)
      ) {
        return lyr.id;
      }
    }
  } catch {
    /* style not ready */
  }
  // Fall back to the base label layer so the film still sits below the trees.
  try {
    if (map.getLayer("road-label")) return "road-label";
  } catch {
    /* ignore */
  }
  return undefined;
};

/**
 * Collect the fuel polygons the 3D layer plants on: treed stands AND grasslands
 * (cells that carry a grassy surface fuel but no tree archetype). Each is tagged
 * with `grass` (0 forest, 1 grass) so the film paints the matching floor tone.
 * Cells with no vegetation classify out and read as a hole in the film.
 */
const collectVegetatedPolygons = (
  map: MapboxMap,
  fuelSourceId: string
): GeoJSON.FeatureCollection => {
  let features: GeoJSON.Feature[] = [];
  try {
    features = map.querySourceFeatures(fuelSourceId, {
      sourceLayer: SOURCE_LAYER,
    }) as unknown as GeoJSON.Feature[];
  } catch {
    return EMPTY;
  }
  const out: GeoJSON.Feature[] = [];
  for (const f of features) {
    // Classification is cached per feature id (shared with the tree layer),
    // so the per-settle JSON.parse of every feature's characteristics is gone.
    const info = getFuelFeatureInfo(map, fuelSourceId, f);
    let grass = 0;
    if (!info.spec) {
      // Not a tree/shrub stand — include it only if it carries grass.
      if (!info.ground || info.ground.grassProb <= 0) continue;
      grass = 1;
    }
    out.push({
      type: "Feature",
      properties: { v: featureVariation(f), grass },
      geometry: f.geometry,
    });
  }
  return { type: "FeatureCollection", features: out };
};

interface MapDimFilmProps {
  active: boolean;
  /** Fuel-load vector source id to read the tree footprint from. */
  fuelSourceId: string;
}

/**
 * Lays a translucent film under the 3D trees — a fill clipped to exactly the
 * fuel polygons that carry trees, so the busy satellite detail beneath the
 * canopy is muted while bare ground / grass stays clear. Sits below every other
 * 2D app layer and below the trees, so only the basemap under the trees is
 * dimmed. Re-derives the footprint as tiles load and the camera moves.
 */
const MapDimFilm: React.FC<MapDimFilmProps> = ({ active, fuelSourceId }) => {
  const { map } = useMap();

  useEffect(() => {
    if (!map) return;

    let cancelled = false;
    let debounce: number | null = null;
    const FILM_ID = filmIdFor(fuelSourceId);
    const FILM_SOURCE_ID = filmSourceIdFor(fuelSourceId);

    const remove = () => {
      try {
        if (map.getLayer(FILM_ID)) map.removeLayer(FILM_ID);
      } catch {
        /* map tearing down */
      }
      try {
        if (map.getSource(FILM_SOURCE_ID)) map.removeSource(FILM_SOURCE_ID);
      } catch {
        /* map tearing down */
      }
    };

    const refresh = () => {
      if (cancelled || !map.getSource(FILM_SOURCE_ID)) return;
      const data = collectVegetatedPolygons(map, fuelSourceId);
      (map.getSource(FILM_SOURCE_ID) as GeoJSONSource).setData(data);
    };

    const scheduleRefresh = () => {
      if (debounce !== null) window.clearTimeout(debounce);
      debounce = window.setTimeout(() => {
        debounce = null;
        refresh();
      }, 200);
    };

    const onSourceData = (e: { sourceId?: string; isSourceLoaded?: boolean }) => {
      if (e.sourceId === fuelSourceId && e.isSourceLoaded) scheduleRefresh();
    };

    const add = () => {
      if (cancelled || !isStyleParsed(map)) return;
      if (!map.getSource(FILM_SOURCE_ID)) {
        map.addSource(FILM_SOURCE_ID, { type: "geojson", data: EMPTY });
      }
      if (!map.getLayer(FILM_ID)) {
        map.addLayer(
          {
            id: FILM_ID,
            type: "fill",
            source: FILM_SOURCE_ID,
            paint: {
              "fill-color": FILM_FILL_COLOR as never,
              "fill-antialias": false,
            },
          },
          bottomMostOverlayId(map)
        );
      }
      refresh();
    };

    if (!active) {
      remove();
      return;
    }

    const attach = () => {
      if (cancelled) return;
      add();
      map.on("moveend", scheduleRefresh);
      map.on("sourcedata", onSourceData);
      map.on("styledata", add);
    };

    const cancelAttach = onStyleReady(map, attach);

    return () => {
      cancelled = true;
      cancelAttach();
      if (debounce !== null) window.clearTimeout(debounce);
      map.off("moveend", scheduleRefresh);
      map.off("sourcedata", onSourceData);
      map.off("styledata", add);
      remove();
    };
  }, [map, active, fuelSourceId]);

  return null;
};

export default MapDimFilm;
